import * as React from "react";
import { Link } from "react-router";
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Eye,
  EyeOff,
  Info,
  Lock,
  Square,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { PASSWORD_POLICY } from "@/api/auth";
import { cn } from "@/lib/cn";

/**
 * The pre-auth form kit (canvas 01b `auth/*`, MV2-907..909).
 *
 * One heading, one field, one input, one password checklist, one code
 * entry, one primary button, one foot row and one banner — the pieces the
 * Login, Forgot and Accept-invite boards are all drawn from, so the three
 * pages stop carrying their own `Field` / `inputCls` / `PasswordChecklist`.
 * Kept in the auth folder: nothing here is meant for authenticated pages,
 * which use `ui/form`.
 */

/* ───────────────────────────────────────────────────────────── heading */

/** `.h1` (24 / 600 / -0.015em) over a 14 px body line, 28 px below. */
export function AuthHeading({
  title,
  sub,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-1.5 pb-7">
      <h1 className="text-[24px] font-semibold leading-[1.15] tracking-[-0.015em] text-gray-900">
        {title}
      </h1>
      {sub ? (
        <p className="text-[14px] leading-[1.45] text-gray-700">{sub}</p>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── field */

/** Label (12 / 500 / gray-800) over a control, with an error or help line. */
export function AuthField({
  label,
  htmlFor,
  children,
  err,
  help,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
  err?: React.ReactNode;
  help?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
      <label
        htmlFor={htmlFor}
        className="text-[12px] font-medium text-gray-800"
      >
        {label}
      </label>
      {children}
      {err ? (
        <span
          role="alert"
          className="text-[11px] leading-[1.4] text-[color:var(--danger)]"
        >
          {err}
        </span>
      ) : help ? (
        <span className="text-[11px] leading-[1.4] text-gray-500">{help}</span>
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── input */

export interface AuthInputProps extends React.ComponentProps<"input"> {
  /** 15 px glyph on the left (`Mail`, `Lock`). */
  icon?: React.ReactNode;
  /** Trailing control (the eye toggle). */
  suffix?: React.ReactNode;
  /** Board `.inp.bad` — danger edge and 3 px halo. */
  invalid?: boolean;
}

/**
 * Board `.inp`: 34 high, gray-300 edge, radius 8, 13 px at `lg` and up.
 * Below `lg` (the same breakpoint `AuthShell` switches at) the row grows to
 * 44 px with 16 px text — a real touch target on a phone, where this is the
 * only control on screen, instead of the desktop-compact 34 px hit area.
 * 16 px specifically: below that, iOS auto-zooms the page on focus.
 */
export const AuthInput = React.forwardRef<HTMLInputElement, AuthInputProps>(
  function AuthInput({ icon, suffix, invalid, className, ...props }, ref) {
    return (
      <div
        className={cn(
          "flex h-11 min-w-0 items-center gap-2 rounded-[8px] border bg-surface px-3 text-[16px] text-gray-900 lg:h-[34px] lg:px-2.5 lg:text-[13px]",
          "transition-[border-color,box-shadow] duration-fast",
          invalid
            ? "border-[color:var(--danger)] shadow-[0_0_0_3px_color-mix(in_srgb,var(--danger)_14%,transparent)]"
            : "border-gray-300 focus-within:border-[color:var(--focus)] focus-within:shadow-[0_0_0_3px_color-mix(in_srgb,var(--focus)_18%,transparent)]",
          className,
        )}
      >
        {icon ? (
          <span aria-hidden className="flex flex-none text-gray-500">
            {icon}
          </span>
        ) : null}
        <input
          ref={ref}
          aria-invalid={invalid || undefined}
          className="tabular min-w-0 flex-1 bg-transparent text-[16px] text-gray-900 outline-none placeholder:text-gray-400 lg:text-[13px]"
          {...props}
        />
        {suffix}
      </div>
    );
  },
);

/** `AuthInput` with the lock glyph and a show / hide toggle. */
export const PasswordInput = React.forwardRef<
  HTMLInputElement,
  Omit<AuthInputProps, "icon" | "suffix" | "type">
>(function PasswordInput(props, ref) {
  const [shown, setShown] = React.useState(false);
  return (
    <AuthInput
      ref={ref}
      type={shown ? "text" : "password"}
      icon={<Lock size={15} />}
      suffix={
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          aria-label={shown ? "Hide password" : "Show password"}
          aria-pressed={shown}
          className="-m-2 flex h-9 w-9 flex-none items-center justify-center text-gray-500 hover:text-gray-900 lg:-m-1 lg:h-auto lg:w-auto"
        >
          {shown ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      }
      {...props}
    />
  );
});

/* ───────────────────────────────────────────────────── password rules */

export interface PasswordRule {
  label: string;
  ok: boolean;
}

/**
 * The four lines every password board draws ("Your password needs"). The
 * Cognito pool policy is `PASSWORD_POLICY`; upper + lower are one line
 * here because that is how the board says it.
 */
export function passwordRules(pw: string): PasswordRule[] {
  return [
    {
      label: `${PASSWORD_POLICY.minLength} characters or more`,
      ok: pw.length >= PASSWORD_POLICY.minLength,
    },
    { label: "Upper and lower case", ok: /[a-z]/.test(pw) && /[A-Z]/.test(pw) },
    { label: "A number", ok: /[0-9]/.test(pw) },
    { label: "A symbol", ok: /[^A-Za-z0-9]/.test(pw) },
  ];
}

export function passwordMeetsPolicy(pw: string): boolean {
  return passwordRules(pw).every((r) => r.ok);
}

/** Board `.detail` block: "Your password needs" + four ticks. Persistent colour, no motion. */
export function PasswordRules({
  password,
  className,
}: {
  password: string;
  className?: string;
}): React.ReactElement {
  const rules = passwordRules(password);
  return (
    <div
      aria-live="polite"
      className={cn(
        "rounded-[8px] border border-gray-200 bg-surface-sunk px-3 py-2.5 text-[12px] leading-[1.5] text-gray-700",
        className,
      )}
    >
      <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[.06em] text-gray-500">
        Your password needs
      </div>
      <ul className="m-0 list-none p-0">
        {rules.map((r) => (
          <li key={r.label} className="flex items-center gap-2 py-0.5">
            <span
              aria-hidden
              className={cn(
                "flex flex-none",
                r.ok ? "text-[color:var(--success)]" : "text-gray-400",
              )}
            >
              {r.ok ? <Check size={15} /> : <Square size={15} />}
            </span>
            <span className={r.ok ? "text-gray-800" : "text-gray-500"}>
              {r.label}
              <span className="sr-only">{r.ok ? " — met" : " — not yet"}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── code boxes */

export interface CodeInputProps {
  value: string;
  onChange: (next: string) => void;
  length?: number;
  /** Board sizes: `lg` = 54×52 / 20 px (TOTP), `md` = 54×48 / 18 px (reset code). */
  size?: "lg" | "md";
  autoFocus?: boolean;
  disabled?: boolean;
  "aria-label": string;
  id?: string;
}

/**
 * Six mono boxes over ONE real `<input inputmode=numeric>`, so paste,
 * autofill (`one-time-code`) and screen readers all see a single field.
 * The box at the caret paints the focus ring.
 */
export function CodeInput({
  value,
  onChange,
  length = 6,
  size = "lg",
  autoFocus,
  disabled,
  id,
  "aria-label": ariaLabel,
}: CodeInputProps): React.ReactElement {
  const [focused, setFocused] = React.useState(false);
  const digits = value.replace(/\D/g, "").slice(0, length);
  const caret = Math.min(digits.length, length - 1);
  return (
    <div className="relative inline-flex gap-2">
      {Array.from({ length }, (_, i) => {
        const on = focused && i === caret;
        return (
          <span
            key={i}
            aria-hidden
            className={cn(
              "flex w-[54px] flex-none items-center justify-center rounded-[8px] border bg-surface font-mono tabular text-gray-900",
              size === "lg" ? "h-[52px] text-[20px]" : "h-12 text-[18px]",
              on
                ? "border-[color:var(--focus)] shadow-[0_0_0_3px_color-mix(in_srgb,var(--focus)_18%,transparent)]"
                : "border-gray-300",
            )}
          >
            {digits[i] ?? ""}
          </span>
        );
      })}
      <input
        id={id}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        aria-label={ariaLabel}
        autoFocus={autoFocus}
        disabled={disabled}
        maxLength={length}
        value={digits}
        onChange={(e) =>
          onChange(e.target.value.replace(/\D/g, "").slice(0, length))
        }
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="absolute inset-0 h-full w-full cursor-text opacity-0"
      />
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── button */

/**
 * Board primary: 42 high, full width, 14 px, label left and the arrow
 * right — at `lg` and up. Below `lg`, 48 high to match the field bump.
 */
export function AuthPrimaryButton({
  children,
  loading,
  loadingLabel,
  className,
  ...props
}: React.ComponentProps<typeof Button> & {
  loading?: boolean;
  loadingLabel?: string;
}): React.ReactElement {
  return (
    <Button
      variant="primary"
      size="md"
      className={cn(
        "h-12 w-full justify-between px-4 text-[14px] disabled:opacity-45 lg:h-[42px]",
        className,
      )}
      disabled={props.disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      <span>{loading ? (loadingLabel ?? children) : children}</span>
      <ArrowRight size={15} aria-hidden />
    </Button>
  );
}

/* ─────────────────────────────────────────────────────────────── foot */

/** Row 28 px under the button: a link left, an 11 px meta right. */
export function AuthFoot({
  left,
  right,
}: {
  left?: React.ReactNode;
  right?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-2 pt-7 text-[12.5px]">
      {left}
      <span className="flex-1" />
      {right ? (
        <span className="text-[11px] leading-[1.4] text-gray-500">{right}</span>
      ) : null}
    </div>
  );
}

/** Action-coloured text link, in-app (`to`) or plain (`href`). */
export function AuthLink({
  to,
  href,
  onClick,
  children,
  className,
}: {
  to?: string;
  href?: string;
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  const cls = cn(
    "text-[color:var(--action)] hover:text-[color:var(--action-hover)] focus-visible:underline focus-visible:outline-none",
    className,
  );
  if (to) {
    return (
      <Link to={to} className={cls} onClick={onClick}>
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} className={cls} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────── banner */

export type AuthBannerTone = "info" | "bad" | "warn";

const BANNER_TONE: Record<
  AuthBannerTone,
  { box: string; ink: string; Icon: typeof Info }
> = {
  info: {
    box: "border-[color:color-mix(in_srgb,var(--info)_28%,transparent)] bg-[color:color-mix(in_srgb,var(--info)_9%,transparent)]",
    ink: "text-[color:var(--info)]",
    Icon: Info,
  },
  bad: {
    box: "border-[color:color-mix(in_srgb,var(--danger)_30%,transparent)] bg-[color:color-mix(in_srgb,var(--danger)_9%,transparent)]",
    ink: "text-[color:var(--danger)]",
    Icon: AlertTriangle,
  },
  warn: {
    box: "border-[color:color-mix(in_srgb,var(--warning)_32%,transparent)] bg-[color:color-mix(in_srgb,var(--warning)_11%,transparent)]",
    ink: "text-[color:var(--warning)]",
    Icon: AlertTriangle,
  },
};

/**
 * Board `.banner`: 10/14 padding, radius 10, 12.5 px, the tone glyph left.
 * `bad` and `warn` announce as alerts; `info` is a status.
 */
export function AuthBanner({
  tone,
  children,
  action,
  align = "center",
  className,
}: {
  tone: AuthBannerTone;
  children: React.ReactNode;
  /** Trailing control (a dismiss X). */
  action?: React.ReactNode;
  align?: "center" | "start";
  className?: string;
}): React.ReactElement {
  const t = BANNER_TONE[tone];
  return (
    <div
      role={tone === "info" ? "status" : "alert"}
      className={cn(
        "flex gap-2.5 rounded-[10px] border px-3.5 py-2.5 text-[12.5px] text-gray-900",
        align === "center" ? "items-center" : "items-start",
        t.box,
        className,
      )}
    >
      <span
        aria-hidden
        className={cn("flex flex-none", t.ink, align === "start" && "mt-0.5")}
      >
        <t.Icon size={15} />
      </span>
      <div className="min-w-0 flex-1">{children}</div>
      {action}
    </div>
  );
}
